import React, { useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Box, CssBaseline, AppBar, Drawer } from "@mui/material";
import { useLocation } from "react-router-dom";

import NavBar from "../../components/NavBar/NavBar";
import DrawerContent from "./DrawerContent";
import { useResponsive } from "../../utils/Perfil/useResponsive";

const drawerWidth = 290;
const collapsedDrawerWidth = 70;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: "#0d1f51",
    zIndex: theme.zIndex.drawer + 0,
}));

const PerfilLayout = ({ children, user, onSignOut, menuItems }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [desktopOpen, setDesktopOpen] = useState(true);
    const location = useLocation();

    const { isMobile, isTabletNarrow, isTabletWide, isDesktop, theme } = useResponsive();

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleDesktopDrawerToggle = () => setDesktopOpen(!desktopOpen);

    const getMainWidth = () => {
        if (isMobile) return "100%";
        if (isTabletNarrow) return `calc(100% - ${desktopOpen ? drawerWidth : collapsedDrawerWidth}px)`;
        if (isTabletWide) return "100%";
        if (isDesktop) return `calc(100% - ${desktopOpen ? drawerWidth : collapsedDrawerWidth}px)`;
        return "100%";
    };

    const getMainML = () => {
        if (isMobile) return 0;
        if (isTabletNarrow) return `${desktopOpen ? drawerWidth : collapsedDrawerWidth}px`;
        return 0;
    };

    const drawerPaperStyle = (width) => ({
        width,
        bgcolor: "#233044",
        boxSizing: "border-box",
        top: '64px',
        height: 'calc(100% - 64px)',
        transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }),
        overflowX: 'hidden',
        border: 'none',
        position: 'fixed',
        left: 0,
    });

    const drawer = (
        <DrawerContent
            user={user}
            menuItems={menuItems}
            desktopOpen={desktopOpen}
            isMobile={isMobile}
            location={location}
            onMobileClose={() => setMobileOpen(false)}
            onDesktopToggle={handleDesktopDrawerToggle}
        />
    );

    return (
        <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* Top Bar */}
        <StyledAppBar position="fixed" sx={{ width: '100%' }}>
            <NavBar 
                user={user}
                onMenuToggle={isMobile ? handleDrawerToggle : handleDesktopDrawerToggle}
                isDrawerOpen={isMobile ? mobileOpen : desktopOpen}
            />
        </StyledAppBar>

        {/* Sidebar */}
        <Box component="nav" sx={{ width: { md: desktopOpen ? drawerWidth : collapsedDrawerWidth }, flexShrink: { md: 0 } }} aria-label="menu lateral">
            {/* Mobile Drawer */}
            <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
                display: isMobile ? 'block' : 'none',
                "& .MuiDrawer-paper": { ...drawerPaperStyle(drawerWidth), top: 0, height: '100%' },
            }}
            >
            {drawer}
            </Drawer>

            {/* Tablet / Desktop Drawer */}
            {(isTabletNarrow || isTabletWide || isDesktop) && (
            <Drawer
                variant="permanent"
                open
                sx={{
                display: 'block',
                "& .MuiDrawer-paper": drawerPaperStyle(desktopOpen ? drawerWidth : collapsedDrawerWidth),
                }}
            >
                {drawer}
            </Drawer>
            )}
        </Box>

        {/* Main content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8, width: getMainWidth(), ml: getMainML() }}>
            {children}
        </Box>
        </Box>
    );
};

PerfilLayout.propTypes = {
    children: PropTypes.node,
    user: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string,
        google_image: PropTypes.string,
    }),
    onSignOut: PropTypes.func,
    menuItems: PropTypes.array.isRequired,
};

export default PerfilLayout;
